import type { KAPLAYCtx } from "kaplay";
import * as C from "./constants";
import { generatePlatformOutlines } from "./platform-outline-generator";
import { Level, Platform, Spike } from "./types";

export function createLevel(k: KAPLAYCtx, levelData: Level) {
  const worldContainer = C.DEBUG_LAMP_AT_START
    ? k.add([k.pos(0, 0), "world"])
    : k.add([
        k.pos(0, 0),
        k.move(k.LEFT, C.PLAYER_SPEED * C.GRID_CELL_SIZE),
        "world",
      ]);

  let objectsRendered = false;

  const groundGroup = k.make();

  const totalGroundWidth = levelData.grid.width * C.GRID_CELL_SIZE + k.width();

  function createGridTexture(width: number, height: number) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const ctx = canvas.getContext("2d")!;

    ctx.fillStyle = `rgb(${C.GROUND_COLOR[0]}, ${C.GROUND_COLOR[1]}, ${C.GROUND_COLOR[2]})`;
    ctx.fillRect(0, 0, width, height);

    const gridSize = C.GRID_CELL_SIZE * C.GRID_TEXTURE_SCALE;
    ctx.strokeStyle = `rgba(${C.ACCENT_COLOR[0]}, ${C.ACCENT_COLOR[1]}, ${C.ACCENT_COLOR[2]}, 0.15)`;
    ctx.lineWidth = 1;

    for (let x = 0; x < width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }

    for (let y = 0; y < height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    return canvas;
  }

  const textureCanvas = createGridTexture(totalGroundWidth, k.height());

  const textureDataUrl = textureCanvas.toDataURL();

  const textureKey = `ground-texture-${Date.now()}`;
  k.loadSprite(textureKey, textureDataUrl);

  groundGroup.add([
    k.sprite(textureKey),
    k.pos(-k.width(), 0),
    k.area(),
    k.body({ isStatic: true }),
    "ground",
  ]);

  groundGroup.add([
    k.rect(totalGroundWidth - C.GROUND_EDGE_WIDTH_OFFSET, C.GROUND_EDGE_HEIGHT),
    k.color(...C.ACCENT_COLOR),
    k.pos(-k.width() + C.GROUND_EDGE_POSITION_OFFSET, 0),
    k.opacity(0.8),
    k.z(3),
  ]);

  groundGroup.use(k.pos(0, C.VISIBLE_GRID_HEIGHT * C.GRID_CELL_SIZE));
  worldContainer.add(groundGroup);

  // Helper function to create a lamp
  function createLamp(parent: any, offsetX = 0) {
    // Lamp post pole
    parent.add([
      k.rect(C.LAMP_POST_WIDTH, C.GRID_CELL_SIZE * C.LAMP_POST_HEIGHT_RATIO),
      k.color(80, 80, 80),
      k.anchor("center"),
      k.pos(offsetX, -C.GRID_CELL_SIZE * C.LAMP_POST_OFFSET_Y),
      k.z(1),
    ]);

    // Lamp light
    const lamp = parent.add([
      k.circle(C.LAMP_LIGHT_RADIUS),
      k.color(255, 255, 160),
      k.anchor("center"),
      k.pos(offsetX, -C.GRID_CELL_SIZE * C.LAMP_LIGHT_OFFSET_Y),
      k.opacity(C.LAMP_OPACITY_MIN),
      k.z(2),
      k.animate(),
      "lamp",
    ]);

    // Start pulsing animation
    const beatInterval = 60 / levelData.bpm;
    const pulseDuration = beatInterval / 2;

    lamp.animate("opacity", [C.LAMP_OPACITY_MIN, C.LAMP_OPACITY_MAX], {
      duration: pulseDuration,
      direction: "ping-pong",
      easing: k.easings.easeOutCubic,
    });

    lamp.animate("scale", [C.LAMP_SCALE_MIN, C.LAMP_SCALE_MAX], {
      duration: pulseDuration,
      direction: "ping-pong",
      easing: k.easings.easeOutCubic,
    });

    return lamp;
  }

  // Debug lamp at the start for timing testing
  if (C.DEBUG_LAMP_AT_START) {
    const debugLampGroup = k.make();
    createLamp(debugLampGroup);
    debugLampGroup.use(
      k.pos(
        C.PLAYER_HORIZONTAL_POSITION * C.GRID_CELL_SIZE,
        (C.VISIBLE_GRID_HEIGHT - 1) * C.GRID_CELL_SIZE,
      ),
    );
    worldContainer.add(debugLampGroup);
  }

  const platforms = levelData.objects.filter(
    (obj) => obj.type === "platform",
  ) as Platform[];
  const outlineSegments = generatePlatformOutlines(platforms);
  const outlineObjects: any[] = [];

  for (const segment of outlineSegments) {
    const outline = worldContainer.add([
      k.rect(
        segment.width * C.GRID_CELL_SIZE,
        segment.height * C.GRID_CELL_SIZE,
      ),
      k.color(...C.ACCENT_COLOR),
      k.anchor("topleft"),
      k.pos(
        segment.x * C.GRID_CELL_SIZE,
        (C.VISIBLE_GRID_HEIGHT - segment.y - segment.height) * C.GRID_CELL_SIZE,
      ),
      k.opacity(1.0),
      k.z(10),
      "platform-outline",
      { worldX: segment.x },
    ]);
    outlineObjects.push(outline);
  }

  function createPlatform(platform: Platform, worldX: number) {
    const platformGroup = k.make();

    platformGroup.add([
      k.rect(
        platform.width * C.GRID_CELL_SIZE,
        platform.height * C.GRID_CELL_SIZE,
      ),
      k.color(...C.PLATFORM_COLOR),
      k.anchor("topleft"),
      k.pos(0, 0),
      k.area(),
      k.body({ isStatic: true }),
      "platform",
    ]);

    // Seeded random lamp post (30% chance)
    const seed = worldX * 1000 + platform.y * 100 + platform.width * 10; // Create deterministic seed
    const random = Math.sin(seed) * 0.5 + 0.5; // Convert to 0-1 range

    // Check if there's a block covering the middle of this platform
    const platformCenterX = worldX + platform.width / 2;
    const platformTopY = platform.y + platform.height;
    const hasBlockAbove = levelData.objects.some(
      (obj) =>
        obj.type === "platform" &&
        obj.x <= platformCenterX &&
        obj.x + obj.width > platformCenterX &&
        obj.y <= platformTopY + 2 && // Check up to 2 units above
        obj.y >= platformTopY,
    );

    if (random < C.LAMP_FREQUENCY && platform.width >= 2 && !hasBlockAbove) {
      createLamp(platformGroup, (platform.width * C.GRID_CELL_SIZE) / 2);
    }

    platformGroup.use(
      k.pos(
        worldX * C.GRID_CELL_SIZE,
        (C.VISIBLE_GRID_HEIGHT - platform.y - platform.height) *
          C.GRID_CELL_SIZE,
      ),
    );
    const finalPlatform = worldContainer.add(platformGroup);
    return finalPlatform;
  }

  function createSpike(spike: Spike, worldX: number) {
    const spikes = [];

    for (let i = 0; i < spike.width; i++) {
      const spikeX = (worldX + i) * C.GRID_CELL_SIZE;
      const spikeY = (C.VISIBLE_GRID_HEIGHT - spike.y) * C.GRID_CELL_SIZE;

      const trianglePoints = [
        k.vec2(0, C.GRID_CELL_SIZE),
        k.vec2(C.GRID_CELL_SIZE, C.GRID_CELL_SIZE),
        k.vec2(C.GRID_CELL_SIZE / 2, 0),
      ];

      const spikeGroup = k.make();

      spikeGroup.add([
        k.polygon(trianglePoints),
        k.color(...C.ACCENT_COLOR),
        k.pos(0, 0),
        k.area(),
        k.body({ isStatic: true }),
        "spike",
      ]);

      // Inset center (darker spike color) - no bottom border
      const insetPoints = [
        k.vec2(C.SPIKE_INSET_SIZE, C.GRID_CELL_SIZE),
        k.vec2(C.GRID_CELL_SIZE - C.SPIKE_INSET_SIZE, C.GRID_CELL_SIZE),
        k.vec2(C.GRID_CELL_SIZE / 2, C.SPIKE_INSET_SIZE),
      ];

      spikeGroup.add([
        k.polygon(insetPoints),
        k.color(...C.SPIKE_COLOR),
        k.pos(0, 0),
        k.z(1),
      ]);

      spikeGroup.use(k.pos(spikeX, spikeY));
      const spikeObject = worldContainer.add(spikeGroup);
      spikes.push(spikeObject);
    }

    return spikes;
  }

  function updateWorld(playerWorldX: number) {
    if (!objectsRendered) {
      for (const levelObject of levelData.objects) {
        if (levelObject.type === "platform") {
          createPlatform(levelObject, levelObject.x);
        } else if (levelObject.type === "spike") {
          createSpike(levelObject, levelObject.x);
        }
      }
      objectsRendered = true;
    }
  }

  function isLevelComplete(playerWorldX: number): boolean {
    return playerWorldX >= levelData.grid.width;
  }

  return { updateWorld, isLevelComplete };
}
